import * as React from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from "@eduviaos/ui-kit";
import {
  Activity as ActivityIcon,
  ArrowRight,
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  CreditCard,
  FileText,
  History,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  StickyNote,
  UserCheck2,
} from "lucide-react";
import type { Activity, ActivityType, AdmissionEnquiry, StaffRosterItem } from "@eduviaos/shared";
import { useStaffRoster } from "@/hooks/useAdmissions";

/**
 * ActivityTimeline — Phase D6.
 *
 * Renders the append-only CRM activity log. Two views:
 *   - "Activities" — only user-logged activities (call/email/etc).
 *   - "All" — includes system breadcrumbs (stage_changed, assigned,
 *     followup_set) so the IA can audit the full lifecycle.
 *
 * Activities sort newest-first inside each tab. Long notes wrap with
 * a `whitespace-pre-wrap` block so multi-paragraph debriefs stay
 * readable without an "expand" affordance.
 *
 * The "Add activity" button is rendered by the parent so it can wire
 * its own dialog state — keeping this component pure makes the
 * "All" tab reusable from the audit / SA portals later.
 */

const ACTIVITY_ICONS: Record<ActivityType, React.ReactNode> = {
  call: <Phone className="size-3.5" aria-hidden="true" />,
  email: <Mail className="size-3.5" aria-hidden="true" />,
  message: <MessageSquare className="size-3.5" aria-hidden="true" />,
  note: <StickyNote className="size-3.5" aria-hidden="true" />,
  document_uploaded: <FileText className="size-3.5" aria-hidden="true" />,
  payment_received: <CreditCard className="size-3.5" aria-hidden="true" />,
  scheduled: <CalendarClock className="size-3.5" aria-hidden="true" />,
  stage_changed: <ArrowRight className="size-3.5" aria-hidden="true" />,
  assigned: <UserCheck2 className="size-3.5" aria-hidden="true" />,
  followup_set: <CheckCheck className="size-3.5" aria-hidden="true" />,
};

const ACTIVITY_TONE: Record<ActivityType, string> = {
  call: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
  email: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
  message: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  note: "bg-muted text-muted-foreground border-border/60",
  document_uploaded: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  payment_received:
    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  scheduled: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
  stage_changed: "bg-primary/15 text-primary border-primary/30",
  assigned: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30",
  followup_set: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
};

const ACTIVITY_LABEL: Record<ActivityType, string> = {
  call: "Call",
  email: "Email",
  message: "Message",
  note: "Note",
  document_uploaded: "Document",
  payment_received: "Payment",
  scheduled: "Scheduled",
  stage_changed: "Stage change",
  assigned: "Assigned",
  followup_set: "Follow-up",
};

const SYSTEM_TYPES = new Set<ActivityType>(["stage_changed", "assigned", "followup_set"]);

function formatRelative(iso: string): string {
  const at = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - at.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return "just now";
  if (diffMs < hour) return `${Math.floor(diffMs / minute)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
  if (diffMs < day * 7) return `${Math.floor(diffMs / day)}d ago`;
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatExact(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function staffDisplay(roster: StaffRosterItem[] | undefined, userId: string | null): string {
  if (!userId) return "system";
  const s = roster?.find((x) => x.user_id === userId);
  if (!s) return userId.slice(0, 8);
  return s.full_name?.trim() || s.email;
}

/**
 * Reconstruct a human-friendly note for system activities. The backend
 * persists raw user IDs in the `note` field (because it has no name
 * cache), so we rewrite them on the client using the staff roster. For
 * non-system rows the original note flows through unchanged.
 */
function humanizeNote(
  activity: Activity,
  roster: StaffRosterItem[] | undefined,
): string | null | undefined {
  if (activity.activity_type === "assigned") {
    const prev = (activity.metadata?.previous_assignee as string | null | undefined) ?? null;
    const next = (activity.metadata?.new_assignee as string | null | undefined) ?? null;
    if (next === null && prev) {
      return `Unassigned (was ${staffDisplay(roster, prev)})`;
    }
    if (prev === null && next) {
      return `Assigned to ${staffDisplay(roster, next)}`;
    }
    if (prev && next) {
      return `Reassigned from ${staffDisplay(roster, prev)} to ${staffDisplay(roster, next)}`;
    }
    return activity.note;
  }
  return activity.note;
}

interface ActivityTimelineProps {
  enquiry: AdmissionEnquiry;
  onAddActivity: () => void;
}

export function ActivityTimeline({ enquiry, onAddActivity }: ActivityTimelineProps) {
  const { data: roster } = useStaffRoster();
  const [view, setView] = React.useState<"user" | "all">("user");

  const all = enquiry.activities;
  // Newest first.
  const ordered = React.useMemo(() => [...all].reverse(), [all]);
  const visible =
    view === "user" ? ordered.filter((a) => !SYSTEM_TYPES.has(a.activity_type)) : ordered;

  return (
    <Card>
      <CardHeader className="border-border/60 flex flex-row items-center justify-between gap-2 border-b pb-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <ActivityIcon className="text-muted-foreground size-4" aria-hidden="true" />
          Activity
          {all.length > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              {all.length}
            </Badge>
          )}
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={onAddActivity}
          className="h-7 px-2 text-xs"
          data-testid="add-activity-trigger"
        >
          <Plus className="size-3.5" aria-hidden="true" />
          Log activity
        </Button>
      </CardHeader>
      <CardContent className="pt-4">
        <Tabs value={view} onValueChange={(v) => setView(v as "user" | "all")} className="mb-4">
          <TabsList className="h-8">
            <TabsTrigger value="user" className="text-xs" data-testid="activity-tab-user">
              Activities
            </TabsTrigger>
            <TabsTrigger value="all" className="text-xs" data-testid="activity-tab-all">
              All events
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {visible.length === 0 ? (
          <EmptyState onAdd={onAddActivity} viewingAll={view === "all"} />
        ) : (
          <ol className="space-y-4">
            {visible.map((a) => (
              <Row
                key={a.id}
                activity={a}
                actor={staffDisplay(roster?.items, a.by)}
                note={humanizeNote(a, roster?.items)}
              />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

interface RowProps {
  activity: Activity;
  actor: string;
  note?: string | null;
}

function Row({ activity, actor, note }: RowProps) {
  const icon = ACTIVITY_ICONS[activity.activity_type];
  const tone = ACTIVITY_TONE[activity.activity_type];
  const label = ACTIVITY_LABEL[activity.activity_type];
  const meta = activity.metadata ?? {};
  const displayNote = note !== undefined ? note : activity.note;

  return (
    <li className="flex gap-3" data-testid={`activity-${activity.id}`}>
      <span
        className={cn(
          "ring-background relative inline-flex size-7 shrink-0 items-center justify-center rounded-full ring-4",
          tone,
        )}
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-foreground text-xs font-medium">
            {label}
            {SYSTEM_TYPES.has(activity.activity_type) && (
              <Badge variant="secondary" className="ml-2 text-[9px]">
                system
              </Badge>
            )}
          </span>
          <span
            className="text-muted-foreground text-[11px] tabular-nums"
            title={formatExact(activity.at)}
          >
            {formatRelative(activity.at)}
            <span className="text-muted-foreground/60 mx-1">·</span>
            by {actor}
          </span>
        </div>
        {displayNote && (
          <p className="text-foreground/90 bg-muted/40 mt-1.5 whitespace-pre-wrap rounded-md px-3 py-2 text-xs leading-relaxed">
            {displayNote}
          </p>
        )}
        {Object.keys(meta).length > 0 && (
          <details className="mt-1.5">
            <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-[11px]">
              Details
            </summary>
            <pre className="text-muted-foreground bg-muted/30 mt-1 overflow-x-auto rounded-md p-2 text-[10px] leading-relaxed">
              {JSON.stringify(meta, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </li>
  );
}

function EmptyState({ onAdd, viewingAll }: { onAdd: () => void; viewingAll: boolean }) {
  return (
    <div className="border-border/60 bg-muted/20 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center">
      <History className="text-muted-foreground size-5" aria-hidden="true" />
      <p className="text-foreground text-xs font-medium">
        {viewingAll ? "No events recorded yet" : "No activities logged yet"}
      </p>
      <p className="text-muted-foreground max-w-sm text-[11px] leading-relaxed">
        {viewingAll
          ? "System events (stage changes, assignments, follow-ups) appear here automatically."
          : "Log a call, email, message, or note to keep the team aligned on every touchpoint."}
      </p>
      {!viewingAll && (
        <Button size="sm" variant="outline" onClick={onAdd} className="mt-2 h-7 px-2 text-xs">
          <CheckCircle2 className="size-3.5" aria-hidden="true" />
          Log first activity
        </Button>
      )}
    </div>
  );
}
